import { Size, Variant } from '../../types';
import { ExecutionContext } from 'styled-components';

export type CommonButtonProps = {
    variant?: Variant;
    disabled?: boolean;
    loading?: boolean;
    size?: Size;
};

function borderWidthPx(theme: ExecutionContext['theme'], size: Size = Size.NORMAL) {
    return theme.componentSizes.button[size].border;
}

export const IGNORE_BUTTON_PROPS = [
    'variant',
    'loading',
    'size',
    'padding',
    'fillParent',
    'showInPrint',
];

export function buttonBackgroundColor(props: CommonButtonProps & ExecutionContext) {
    const { variant, disabled, loading, theme } = props;

    if (disabled || loading) {
        return theme.background.disabled;
    }

    switch (variant) {
        case Variant.PRIMARY:
            return theme.componentColors.button.primary.bgDefault;
        case Variant.PRIMARY_ALTERNATIVE:
            return theme.componentColors.button.primary.bgAlternativeDefault;
        case Variant.SECONDARY:
        case Variant.SECONDARY_ALTERNATIVE:
            return 'transparent';
        case Variant.ATTENTION:
            return theme.primitiveColors.urbanOrange[200];
        case Variant.TERTIARY:
        case Variant.TEXT:
        case Variant.A11Y:
            return 'transparent';
        case Variant.IMPORTANT:
            return theme.componentColors.button.important.bgImportant;
        case Variant.GRAYSCALE:
            return theme.background.alternative;
        default:
            return theme.buttonColorSecondary;
    }
}

export function buttonColor(props: CommonButtonProps & ExecutionContext) {
    const { variant, disabled, loading, theme } = props;

    if (disabled || loading) {
        return theme.foreground.disabled;
    }
    switch (variant) {
        case Variant.PRIMARY:
            return theme.componentColors.button.primary.fgDefault;
        case Variant.PRIMARY_ALTERNATIVE:
            return theme.componentColors.button.primary.fgAlternativeDefault;
        case Variant.SECONDARY:
            return theme.componentColors.button.secondary.fgDefault;
        case Variant.SECONDARY_ALTERNATIVE:
            return theme.componentColors.button.secondary.fgAlternativeDefault;
        case Variant.IMPORTANT:
            return theme.componentColors.button.important.fgImportant;
        default:
            return theme.foreground.default;
    }
}

/**
 * Returns a complete CSS `border` value (e.g. `'2px solid #43434A'` or `'none'`).
 * Width comes from `theme.componentSizes.button[size].border` so it scales with
 * the size variant.
 */
export function buttonBorder(props: CommonButtonProps & ExecutionContext) {
    const color = borderColor(props);

    return color ? `${borderWidthPx(props.theme, props.size)}px solid ${color}` : 'none';
}

function borderColor({ variant, theme }: CommonButtonProps & ExecutionContext) {
    switch (variant) {
        case Variant.SECONDARY:
            return theme.componentColors.button.secondary.borderDefault;
        case Variant.SECONDARY_ALTERNATIVE:
            return theme.border.light;
        default:
            return undefined;
    }
}

/** The variants Figma darkens the fill of when pressed. A secondary has no fill of its own, so it
 *  gets the overlay alone; tertiary and secondary alternative keep theirs transparent. */
const PRESSED_FILL_VARIANTS = [
    Variant.PRIMARY,
    Variant.PRIMARY_ALTERNATIVE,
    Variant.SECONDARY,
    Variant.ATTENTION,
    Variant.IMPORTANT,
    Variant.GRAYSCALE,
];

/**
 * Figma's pressed state: `effects.pressed` (a translucent black) laid over the fill, and over the
 * border of the outlined variants. Returns the declarations for an `:active` rule, or nothing for
 * a button that cannot be pressed. A border is a single colour, so the overlay
 * (black, as in both themes) is mixed into it.
 */
export function buttonPressed(props: CommonButtonProps & ExecutionContext) {
    const { variant, disabled, loading, theme } = props;

    if (disabled || loading) {
        return '';
    }
    const pressed = theme.effects.pressed;
    const fill = PRESSED_FILL_VARIANTS.includes(variant ?? Variant.PRIMARY)
        ? `background-image: linear-gradient(${pressed}, ${pressed});`
        : '';
    const border =
        variant === Variant.SECONDARY || variant === Variant.SECONDARY_ALTERNATIVE
            ? `border-color: color-mix(in srgb, black ${overlayPercent(pressed)}%, ${borderColor(props)});`
            : '';

    return fill + border;
}

/** The alpha of an `rgba(...)` overlay as a percentage: how much of it covers what is beneath. */
function overlayPercent(rgba: string) {
    const alpha = Number(/,\s*([\d.]+)\s*\)$/.exec(rgba)?.[1] ?? 0);
    return Math.round(alpha * 100);
}
